import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { MapLayerMouseEvent, useMap } from 'react-map-gl';
import {
    ImageCoords,
    ImageLayerState,
    LngLat,
} from '@features/map-layers-list/new-layer-file-uploader/model/types';
import {
    coordsToRect,
    handlesGeoJSON,
    rectToCoords,
    resizeFromCorner,
    rotateFromCorner,
} from '@features/map-layers-list/new-layer-file-uploader/model/utils';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectLayersClickabilityById } from '@redux/selectors/selectors';

type Props = {
    children: React.ReactNode;
};

type ImageEditorContextValue = {
    coords: Record<string, ImageCoords> | null;
    setImageLayerCoords: (coordsByLayerId: Record<string, ImageCoords>) => void;
    handlesGeoJSON: (layerId: string) => GeoJSON.FeatureCollection | null;
    mapHandlers: {
        onMouseDown: (e: MapLayerMouseEvent) => void;
        onMouseMove: (e: MapLayerMouseEvent) => void;
        onMouseUp: () => void;
    };
    addImageLayerUrl: (url: string) => void;
    revokeImageLayersUrls: () => void;
};

const ImageLayerContext = createContext<ImageEditorContextValue | null>(null);

export function useImageLayer() {
    const ctx = useContext(ImageLayerContext);
    if (!ctx) {
        throw new Error('useImageEditor must be used inside ImageEditorProvider');
    }
    return ctx;
}

export function ImageLayerProvider({ children }: Props) {
    const [layerIds, setLayerIds] = useState<string[]>([]);
    const [rectState, setRectState] = useState<Record<string, ImageLayerState> | null>(null);

    const imageLayersUrls = useRef<string[]>([]);

    const draggingRef = useRef(false);
    const startCoordsRef = useRef<Record<string, ImageLayerState> | null>(null);
    const startRectRef = useRef<Record<string, ImageLayerState> | null>(null);
    const mouseDownPointRef = useRef<LngLat | null>(null);
    const activeHandleRef = useRef<number | null>(null);

    const layersClickabilityById = useTypedSelector(selectLayersClickabilityById);

    const { mapboxMapId } = useMap();

    const coords = useMemo(
        () =>
            rectState
                ? layerIds.reduce((acc, layerId) => {
                      acc[layerId] = rectToCoords(rectState[layerId]);
                      return acc;
                  }, {})
                : null,
        [layerIds, rectState]
    );

    const addImageLayerUrl = useCallback((url: string) => {
        imageLayersUrls.current.push(url);
    }, []);

    const revokeImageLayersUrls = useCallback(() => {
        imageLayersUrls.current.forEach(url => URL.revokeObjectURL(url));
    }, []);

    const layerCoordsToRectState = useCallback((coordsByLayerId: Record<string, ImageCoords>) => {
        const _layerIds = Object.keys(coordsByLayerId);
        setLayerIds(prev => [...prev, ..._layerIds]);
        setRectState(prev => {
            return {
                ...(prev ? prev : {}),
                ..._layerIds.reduce((acc, id) => {
                    acc[id] = coordsToRect(coordsByLayerId[id]);
                    return acc;
                }, {}),
            };
        });
    }, []);

    const getResizerGeoJson = useCallback(
        (layerId: string) => {
            return coords?.[layerId] ? handlesGeoJSON(coords[layerId]) : null;
        },
        [coords]
    );

    useEffect(() => {
        return () => {
            revokeImageLayersUrls();
        };
    }, [revokeImageLayersUrls]);

    useEffect(() => {
        const map = mapboxMapId?.getMap();
        if (!map) {
            return;
        }

        if (layerIds.some(id => layersClickabilityById[id])) {
            map.dragPan.disable();
            map.doubleClickZoom.disable();
        } else {
            map.dragPan.enable();
            map.doubleClickZoom.enable();
        }
    }, [layerIds, layersClickabilityById, mapboxMapId]);

    const dragLayer = useCallback(
        (curPosition: LngLat) => {
            const [lng, lat] = curPosition;
            const [prevLng, prevLat] = mouseDownPointRef.current ?? ([0, 0] as const);

            const diffLng = lng - prevLng;
            const diffLat = lat - prevLat;
            mouseDownPointRef.current = [lng, lat];

            if (
                !startRectRef.current ||
                !mouseDownPointRef.current ||
                activeHandleRef.current === null
            ) {
                return;
            }

            setRectState(prev => {
                return prev
                    ? layerIds.reduce((acc, id) => {
                          const layerState = prev[id];

                          if (layersClickabilityById[id]) {
                              acc[id] = {
                                  ...layerState,
                                  center: [
                                      layerState.center[0] + diffLng,
                                      layerState.center[1] + diffLat,
                                  ],
                              };
                          } else {
                              acc[id] = layerState;
                          }
                          return acc;
                      }, {})
                    : prev;
            });
        },
        [layerIds, layersClickabilityById, setRectState]
    );

    const rotateLayer = useCallback(
        (curPosition: LngLat) => {
            if (startRectRef.current && mouseDownPointRef.current) {
                setRectState(prev => {
                    return {
                        ...(prev
                            ? layerIds.reduce((acc, id) => {
                                  if (startRectRef.current?.[id] && layersClickabilityById[id]) {
                                      acc[id] = rotateFromCorner(
                                          startRectRef.current[id],
                                          mouseDownPointRef.current!,
                                          curPosition
                                      );
                                  } else {
                                      acc[id] = prev[id];
                                  }
                                  return acc;
                              }, {})
                            : {}),
                    };
                });
            }
        },
        [layerIds, layersClickabilityById]
    );

    const resizeLayerByCorner = useCallback(
        (curPosition: LngLat) => {
            setRectState(prev => {
                return {
                    ...(prev
                        ? layerIds.reduce((acc, id) => {
                              if (
                                  startRectRef.current?.[id] &&
                                  activeHandleRef.current &&
                                  mouseDownPointRef.current &&
                                  Number.isInteger(activeHandleRef.current) &&
                                  layersClickabilityById[id]
                              ) {
                                  acc[id] = resizeFromCorner(
                                      startRectRef.current[id],
                                      activeHandleRef.current,
                                      mouseDownPointRef.current,
                                      curPosition
                                  );
                              } else {
                                  acc[id] = prev[id];
                              }
                              return acc;
                          }, {})
                        : {}),
                };
            });
        },
        [layerIds, layersClickabilityById]
    );

    const onMouseDown = useCallback(
        (e: MapLayerMouseEvent) => {
            const {
                features,
                originalEvent: { shiftKey },
            } = e;

            const { properties } = features?.[0] ?? {};

            activeHandleRef.current = properties?.index;

            if (!Number.isInteger(activeHandleRef.current) && !shiftKey) {
                draggingRef.current = true;
            }

            const { lng, lat } = e.lngLat;
            mouseDownPointRef.current = [lng, lat];

            startCoordsRef.current = coords;
            startRectRef.current = rectState;
        },
        [rectState, coords]
    );

    const onMouseMove = useCallback(
        (e: MapLayerMouseEvent) => {
            if (!draggingRef.current && !startRectRef.current && !mouseDownPointRef.current) {
                return;
            }

            const {
                lngLat,
                originalEvent: { shiftKey },
            } = e;

            const curMouse: LngLat = lngLat.toArray() as LngLat;

            if (draggingRef.current && !shiftKey && !Number.isInteger(activeHandleRef.current)) {
                dragLayer(curMouse);
            } else if (shiftKey && Number.isInteger(activeHandleRef.current)) {
                rotateLayer(curMouse);
            } else {
                resizeLayerByCorner(curMouse);
            }
        },
        [dragLayer, rotateLayer, resizeLayerByCorner, resizeLayerByCorner]
    );

    const onMouseUp = useCallback(() => {
        draggingRef.current = false;
        activeHandleRef.current = null;
        mouseDownPointRef.current = null;
        startCoordsRef.current = null;
    }, []);

    const value: ImageEditorContextValue = {
        coords,
        setImageLayerCoords: layerCoordsToRectState,
        handlesGeoJSON: getResizerGeoJson,
        mapHandlers: {
            onMouseDown,
            onMouseMove,
            onMouseUp,
        },
        addImageLayerUrl,
        revokeImageLayersUrls,
    };

    return <ImageLayerContext.Provider value={value}>{children}</ImageLayerContext.Provider>;
}
